/**
 * useItemBoardColumns(api, itemId, boardId) → { loading, error, boardId, boardName, columns }
 *
 * The columns of the board the current ITEM lives on — what a group template's
 * item rows are authored against. In a board view there is no item: the board
 * is read by `boardId` instead. (useBoardColumns reaches the subitems board
 * instead; a group template needs both.) Column settings are parsed with the
 * same parser so status cells get their colours.
 */
import { useEffect, useState } from 'react';
import { COLUMN_SETTINGS_FIELDS, columnSettingsOf, describeApiErrors } from './useBoardColumns';

export const ITEM_BOARD_QUERY = `query ($itemId: [ID!]) {
  items (ids: $itemId) { board { id name columns { id title type ${COLUMN_SETTINGS_FIELDS} } } }
}`;

export const BOARD_COLUMNS_QUERY = `query ($boardId: [ID!]) {
  boards (ids: $boardId) { id name columns { id title type ${COLUMN_SETTINGS_FIELDS} } }
}`;

/** The board itself, by id — the board view's way to the same columns. */
export async function fetchBoardById(api, boardId) {
  const res = await api.api(BOARD_COLUMNS_QUERY, { variables: { boardId: [String(boardId)] } });
  const error = describeApiErrors(res);
  if (error) throw new Error(error);
  const board = res && res.data && res.data.boards && res.data.boards[0];
  if (!board || !Array.isArray(board.columns)) throw new Error('The board could not be read');
  return board;
}

export async function fetchItemBoard(api, itemId) {
  const res = await api.api(ITEM_BOARD_QUERY, { variables: { itemId: [String(itemId)] } });
  const error = describeApiErrors(res);
  if (error) throw new Error(error);
  const board = res && res.data && res.data.items && res.data.items[0] && res.data.items[0].board;
  if (!board || !Array.isArray(board.columns)) throw new Error('The item board could not be read');
  return board;
}

export default function useItemBoardColumns(api, itemId, boardId) {
  const [state, setState] = useState({ loading: true, error: null, boardId: null, boardName: '', columns: [] });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, boardId: null, boardName: '', columns: [] });
    (itemId ? fetchItemBoard(api, itemId) : fetchBoardById(api, boardId))
      .then((board) => {
        if (!alive) return;
        const columns = (board.columns || [])
          // the Subitems column links to the subitems board; it holds no item value
          .filter((c) => c.type !== 'name' && c.type !== 'subtasks')
          .map((c) => ({ id: c.id, title: c.title, type: c.type, settings: columnSettingsOf(c) }));
        setState({ loading: false, error: null, boardId: board.id, boardName: board.name || '', columns });
      })
      .catch((e) => {
        console.error('Could not load the item board columns', e);
        if (!alive) return;
        setState((prev) => ({ ...prev, loading: false, error: e }));
      });
    return () => {
      alive = false;
    };
  }, [api, itemId, boardId]);

  return state;
}
